import React from 'react';
import SkeletonBlock from '../ui/SkeletonBlock';
import SkeletonRoot from '../ui/SkeletonRoot';
import {
  adminFilterToolbarClass,
  adminListTableShellClass,
  adminListTableSkeletonHeaderClass,
  adminListTableSkeletonRowClass,
} from '../../utils/admin/adminChrome';

const DEFAULT_FILTER_WIDTHS = [
  'min-w-[200px] flex-1 max-w-sm',
  'w-[180px]',
  'w-[160px]',
];

export interface AdminListTableSkeletonProps {
  filterSlotCount?: number;
  rowCount?: number;
  showTopActionsSlot?: boolean;
  showPageShell?: boolean;
  /** Skip SkeletonRoot when parent already provides a11y shell */
  embedded?: boolean;
  loadingLabel?: string;
}

const AdminListTableSkeleton: React.FC<AdminListTableSkeletonProps> = ({
  filterSlotCount = 3,
  rowCount = 5,
  showTopActionsSlot = false,
  showPageShell = true,
  embedded = false,
  loadingLabel = 'Loading content',
}) => {
  const filterWidths = [...DEFAULT_FILTER_WIDTHS];
  while (filterWidths.length < filterSlotCount) {
    filterWidths.push('w-[140px]');
  }
  const resolvedFilterWidths = filterWidths.slice(0, filterSlotCount);

  const tableBlock = (
    <div className={adminListTableShellClass}>
      <div className={adminListTableSkeletonHeaderClass}>
        <SkeletonBlock className="h-4 w-full" />
      </div>
      {Array.from({ length: rowCount }).map((_, i) => (
        <div key={i} className={adminListTableSkeletonRowClass}>
          <SkeletonBlock className="h-4 w-32" />
          <SkeletonBlock className="h-4 w-24" />
          <SkeletonBlock className="h-4 w-20" />
          <SkeletonBlock className="h-4 w-16" />
          <SkeletonBlock className="ml-auto h-4 w-12" />
        </div>
      ))}
    </div>
  );

  const filtersBlock = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <SkeletonBlock className="h-5 w-36" />
        {showTopActionsSlot ? <SkeletonBlock className="h-9 w-[130px]" /> : null}
      </div>
      <div className={adminFilterToolbarClass}>
        {resolvedFilterWidths.map((widthClass, index) => (
          <SkeletonBlock key={index} className={`h-9 rounded-lg ${widthClass}`} />
        ))}
      </div>
    </div>
  );

  if (!showPageShell) {
    const inner = (
      <div className="space-y-4">
        {filtersBlock}
        {tableBlock}
      </div>
    );
    if (embedded) {
      return inner;
    }
    return (
      <SkeletonRoot loadingLabel={loadingLabel} className="space-y-4">
        {filtersBlock}
        {tableBlock}
      </SkeletonRoot>
    );
  }

  return (
    <SkeletonRoot loadingLabel={loadingLabel}>
      <div className="min-h-screen bg-white p-4 sm:p-6">
        <div className="mx-auto max-w-[1400px] space-y-4">
          <SkeletonBlock className="h-8 w-48 rounded-lg" />
          <div className="rounded-2xl border border-primary-500/10 bg-white p-4 shadow-sm">
            {filtersBlock}
          </div>
          {tableBlock}
        </div>
      </div>
    </SkeletonRoot>
  );
};

export default AdminListTableSkeleton;
